import React, { useState, useEffect } from 'react';
import Dialog from '../../../../common/element/Dialog';
import BtnPrimary from '../../../../common/element/Buttons/btnPrimary';
import ThreeDotLoader from "../../../../common/element/ButtonLoader/ThreeDotLoader";
import Select from '../../../../common/element/Select';
import { useDispatch, useSelector } from 'react-redux';
import {
    addAssignments,
    updateAssignments,
    clearAddAssignments,
    clearUpdateAssignments
} from '../../../../redux/Assignment/assignmentsSlice';
import SweetAlertService from '../../../../common/SweetAlert/SweetAlert';
import {
    selectAddAssignmentsData,
    selectAddAssignmentsError,
    selectUpdateAssignmentsData,
    selectUpdateAssignmentsError
} from '../../../../redux/Assignment/assignmentsSelector';
import { selectRoleListingData } from '../../../../redux/auth/authSelector';
import { fetchRoleListing } from '../../../../redux/auth/authSlice';

const BLANK_QUESTION = () => ({ question_type: "simple", main_question: "", sub_questions: [""] });

const INITIAL_FORM_STATE = {
    activityName: "",
    accessFor: [],
    questions: [BLANK_QUESTION()]
};

const INITIAL_ERROR_STATE = {
    activityName: "",
    accessFor: "",
    questions: [{ main_question: "", sub_questions: [""] }]
};

const SOS_GROUP_ID = 4; // Hardcoded ID for Science of Self
const SALES_TRAINING_GROUP_ID = 6; // Sales Training Companies
const ALL_ACCESS_GROUP_ID = 3; // ID for "All" option

const AddEditAssignments = ({
    dialogOpen,
    onDialogClose,
    activityData,
    mode = 'add',
    onAdded,
    handleRefresh
}) => {
    const dispatch = useDispatch();

    const addData = useSelector(selectAddAssignmentsData);
    const addError = useSelector(selectAddAssignmentsError);
    const updateData = useSelector(selectUpdateAssignmentsData);
    const updateError = useSelector(selectUpdateAssignmentsError);
    const roles = useSelector(selectRoleListingData);

    const isView = mode === 'view';
    const isEdit = mode === 'edit';

    const [buttonDisabled, setButtonDisabled] = useState(false);
    
    // New State for Radio selection
    const [accessCategory, setAccessCategory] = useState('session'); // 'session' | 'sos' | 'stc'
    
    const [formData, setFormData] = useState(INITIAL_FORM_STATE);
    const [formError, setFormErrors] = useState(INITIAL_ERROR_STATE);

    // Fetch roles on mount
    useEffect(() => {
        dispatch(fetchRoleListing());
    }, [dispatch]);

    // Map roles to access options (Excluding Science of Self ID 4 and Sales Training ID 6 from the checkbox list)
    const accessOptions = roles
        ?.filter(group => group.access_group_id !== SOS_GROUP_ID && group.access_group_id !== SALES_TRAINING_GROUP_ID)
        .map(group => ({
            value: group.access_group_id,
            label: group.access_group_name
        }));

    const allAccessIds = accessOptions?.map(opt => opt?.value);

    // Initialize form data when dialog opens
    useEffect(() => {
        if (!dialogOpen) {
            setFormData(INITIAL_FORM_STATE);
            setFormErrors(INITIAL_ERROR_STATE);
            setAccessCategory('session');
            setButtonDisabled(false);
            return;
        }

        if (activityData && (isEdit || isView)) {
            let mappedAccessFor = [];

            if (Array.isArray(activityData.access_for)) {
                mappedAccessFor = activityData.access_for.map(
                    item => item.access_group_id
                );
            } else if (Array.isArray(activityData.access_group_ids)) {
                mappedAccessFor = activityData.access_group_ids;
            } else if (Array.isArray(activityData.accessFor)) {
                mappedAccessFor = activityData.accessFor;
            }

            // Check if Science of Self (ID 4) or Sales Training (ID 6) is selected
            if (mappedAccessFor.includes(SOS_GROUP_ID)) {
                setAccessCategory('sos');
                mappedAccessFor = [SOS_GROUP_ID];
            } else if (mappedAccessFor.includes(SALES_TRAINING_GROUP_ID)) {
                setAccessCategory('stc');
                mappedAccessFor = [SALES_TRAINING_GROUP_ID];
            } else {
                setAccessCategory('session');
                // Logic to handle "All" checkbox visual state for Session roles
                if (mappedAccessFor.includes(ALL_ACCESS_GROUP_ID)) {
                    // If "All" is selected, visually select all other session roles too
                    mappedAccessFor = Array.from(
                        new Set([...mappedAccessFor, ...allAccessIds])
                    );
                }
            }

            const mappedQuestions = Array.isArray(activityData.questions)
                ? activityData.questions.map(q => {
                    const hasSubs = Array.isArray(q.sub_questions) && q.sub_questions.length > 0;
                    return {
                        question_type: q.question_type ?? (hasSubs ? "with_sub" : "simple"),
                        main_question: q.question_text ?? q ?? "",
                        sub_questions: hasSubs ? q.sub_questions : [""]
                    };
                })
                : [BLANK_QUESTION()];

            setFormData({
                activityName:
                    activityData.assignment_name ||
                    activityData.activityName ||
                    "",
                accessFor: mappedAccessFor,
                questions: mappedQuestions
            });
        } else {
            setFormData(INITIAL_FORM_STATE);
            setAccessCategory('session');
        }

        setFormErrors(INITIAL_ERROR_STATE);
    }, [dialogOpen, activityData, isEdit, isView]);


    // Handle API responses
    const handleSuccess = (data, clearAction, successMessage) => {
        if (!data?.success) return;

        onDialogClose();
        setTimeout(() => {
            SweetAlertService.showSuccess({
                title: 'Success!',
                message: data.message || successMessage,
                onConfirm: () => {
                    dispatch(clearAction());
                    onAdded?.();
                    handleRefresh();
                    setButtonDisabled(false);
                }
            });
        }, 200);
    };

    const handleError = (error, clearAction, defaultMessage) => {
        if (!error) return;

        SweetAlertService.showError({
            title: 'Error!',
            message: error.message || error || defaultMessage,
            onConfirm: () => {
                dispatch(clearAction());
                setButtonDisabled(false);
            }
        });
    };

    useEffect(() => {
        handleSuccess(addData, clearAddAssignments, 'Assignment has been added successfully.');
    }, [addData]);

    useEffect(() => {
        handleSuccess(updateData, clearUpdateAssignments, 'Assignment has been updated successfully.');
    }, [updateData]);

    useEffect(() => {
        handleError(addError, clearAddAssignments, 'Failed to add Assignment.');
    }, [addError]);

    useEffect(() => {
        handleError(updateError, clearUpdateAssignments, 'Failed to update Assignment.');
    }, [updateError]);

    // Form handlers
    const handleInputChange = (field, value) => {
        if (isView) return;

        setFormData(prev => ({ ...prev, [field]: value }));

        if (formError[field]) {
            setFormErrors(prev => ({ ...prev, [field]: "" }));
        }
    };

    // Category Change (Session vs SOS)
    const handleCategoryChange = (category) => {
        if (isView) return;
        setAccessCategory(category);

        if (category === 'sos') {
            handleInputChange("accessFor", [SOS_GROUP_ID]);
        } else if (category === 'stc') {
            handleInputChange("accessFor", [SALES_TRAINING_GROUP_ID]);
        } else {
            handleInputChange("accessFor", []);
        }
    };

    const handleAccessChange = (value) => {
        if (isView) return;

        let newAccessFor = [...formData.accessFor];
        const otherIds = allAccessIds.filter(id => id !== ALL_ACCESS_GROUP_ID);

        if (value === ALL_ACCESS_GROUP_ID) {
            // Toggle all selections
            newAccessFor = newAccessFor.includes(ALL_ACCESS_GROUP_ID) ? [] : allAccessIds;
        } else {
            // Toggle individual selection
            newAccessFor = newAccessFor.includes(value)
                ? newAccessFor.filter(id => id !== value)
                : [...newAccessFor, value];

            // Auto-manage "All" checkbox
            const allOthersSelected = otherIds.every(id => newAccessFor.includes(id));

            if (allOthersSelected && !newAccessFor.includes(ALL_ACCESS_GROUP_ID)) {
                newAccessFor.push(ALL_ACCESS_GROUP_ID);
            } else if (!allOthersSelected) {
                newAccessFor = newAccessFor.filter(id => id !== ALL_ACCESS_GROUP_ID);
            }
        }

        handleInputChange("accessFor", newAccessFor);
    };

    const handleQuestionTypeChange = (mainIdx, type) => {
        if (isView) return;
        const newQuestions = formData.questions.map((q, i) =>
            i === mainIdx ? { ...q, question_type: type, sub_questions: [""] } : q
        );
        setFormData(prev => ({ ...prev, questions: newQuestions }));
    };

    const handleMainQuestionChange = (mainIdx, value) => {
        if (isView) return;
        const newQuestions = formData.questions.map((q, i) =>
            i === mainIdx ? { ...q, main_question: value } : q
        );
        setFormData(prev => ({ ...prev, questions: newQuestions }));
        if (formError.questions[mainIdx]?.main_question) {
            const errs = [...formError.questions];
            errs[mainIdx] = { ...errs[mainIdx], main_question: "" };
            setFormErrors(prev => ({ ...prev, questions: errs }));
        }
    };

    const handleSubQuestionChange = (mainIdx, subIdx, value) => {
        if (isView) return;
        const newQuestions = formData.questions.map((q, i) => {
            if (i !== mainIdx) return q;
            const subs = q.sub_questions.map((s, j) => j === subIdx ? value : s);
            return { ...q, sub_questions: subs };
        });
        setFormData(prev => ({ ...prev, questions: newQuestions }));
        if (formError.questions[mainIdx]?.sub_questions?.[subIdx]) {
            const errs = [...formError.questions];
            const subErrs = [...(errs[mainIdx].sub_questions || [])];
            subErrs[subIdx] = "";
            errs[mainIdx] = { ...errs[mainIdx], sub_questions: subErrs };
            setFormErrors(prev => ({ ...prev, questions: errs }));
        }
    };

    const addMainQuestion = () => {
        if (isView) return;
        setFormData(prev => ({ ...prev, questions: [...prev.questions, BLANK_QUESTION()] }));
        setFormErrors(prev => ({
            ...prev,
            questions: [...prev.questions.map(() => ({ main_question: "", sub_questions: [""] })), { main_question: "", sub_questions: [""] }]
        }));
    };

    const removeMainQuestion = (mainIdx) => {
        if (isView || formData.questions.length <= 1) return;
        setFormData(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== mainIdx) }));
        setFormErrors(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== mainIdx) }));
    };

    const addSubQuestion = (mainIdx) => {
        if (isView) return;
        const newQuestions = formData.questions.map((q, i) =>
            i === mainIdx ? { ...q, sub_questions: [...q.sub_questions, ""] } : q
        );
        setFormData(prev => ({ ...prev, questions: newQuestions }));
    };

    const removeSubQuestion = (mainIdx, subIdx) => {
        if (isView || formData.questions[mainIdx].sub_questions.length <= 1) return;
        const newQuestions = formData.questions.map((q, i) => {
            if (i !== mainIdx) return q;
            return { ...q, sub_questions: q.sub_questions.filter((_, j) => j !== subIdx) };
        });
        setFormData(prev => ({ ...prev, questions: newQuestions }));
    };

    const validateForm = () => {
        const errors = {
            activityName: "",
            accessFor: "",
            questions: formData.questions.map((q) => ({
                main_question: !q.main_question.trim() ? "Question is required" : "",
                sub_questions: q.question_type === "with_sub"
                    ? q.sub_questions.map((sq) => !sq.trim() ? "Sub question is required" : "")
                    : []
            }))
        };

        if (!formData.activityName.trim()) {
            errors.activityName = "Assignment name is required";
        }

        if (formData.accessFor.length === 0) {
            errors.accessFor = "At least one access option is required";
        }

        setFormErrors(errors);

        const hasQuestionErrors = errors.questions.some(
            q => q.main_question || q.sub_questions.some(sq => sq)
        );
        return !errors.activityName && !errors.accessFor && !hasQuestionErrors;
    };

    const handleSubmit = () => {
        if (!validateForm()) return;

        setButtonDisabled(true);

        // Determine correct IDs to send
        let payloadAccessGroupIds = [];
        
        if (accessCategory === 'sos') {
            payloadAccessGroupIds = [SOS_GROUP_ID];
        } else if (accessCategory === 'stc') {
            payloadAccessGroupIds = [SALES_TRAINING_GROUP_ID];
        } else {
            // If "All" is selected in Session mode, send only "All" ID (3), otherwise send selected IDs
            payloadAccessGroupIds = formData.accessFor.includes(ALL_ACCESS_GROUP_ID)
                ? [ALL_ACCESS_GROUP_ID]
                : formData.accessFor;
        }

        const payload = {
            assignment_name: formData.activityName,
            questions: formData.questions.map(q => ({
                question_text: q.main_question,
                question_type: q.question_type,
                sub_questions: q.question_type === "with_sub" ? q.sub_questions : []
            })),
            access_group_ids: payloadAccessGroupIds
        };

        if (isEdit) {
            const id =
                activityData?.id ||
                activityData?._id ||
                activityData?.assignment_id;

            dispatch(updateAssignments({ id, data: payload }));
        } else {
            dispatch(addAssignments(payload));
        }
    };

    const getTitle = () => {
        if (isView) return "View Assignment";
        if (isEdit) return "Edit Assignment";
        return "Add Assignment";
    };

    return (
        <Dialog
            isOpen={dialogOpen}
            onClose={onDialogClose}
            width={800}
            onRequestClose={onDialogClose}
            bodyOpenClassName="overflow-hidden"
        >
            {/* Header */}
            <div className="flex border-b mb-5 pb-5 px-6 pt-6 items-center justify-between">
                <div className="flex items-center">
                    <span className="h-8 w-8 border rounded-lg border-gray-200 flex items-center justify-center shadow-sm mr-4">
                        <svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19.0909 5.1434H15.0318L13.4864 2.95331C13.3919 2.81973 13.2596 2.71384 13.1045 2.64769C12.9494 2.58154 12.7777 2.55779 12.609 2.57914C12.4403 2.60049 12.2813 2.66608 12.1501 2.76845C12.019 2.87082 11.921 3.00585 11.8673 3.15817L10.1273 8.08011L8.15455 0.649202C8.10679 0.469177 7.99837 0.308625 7.84557 0.19167C7.69277 0.074715 7.50384 0.00767393 7.30718 0.000619819C7.11051 -0.00643429 6.91673 0.0468798 6.75495 0.152551C6.59316 0.258222 6.47212 0.410544 6.41 0.586628L4.79909 5.1434H0.909091C0.667985 5.1434 0.436754 5.23371 0.266267 5.39447C0.0957789 5.55522 0 5.77325 0 6.00059C0 6.22792 0.0957789 6.44595 0.266267 6.6067C0.436754 6.76746 0.667985 6.85777 0.909091 6.85777H5.45455C5.64542 6.85781 5.83147 6.8012 5.98632 6.69597C6.14117 6.59073 6.25696 6.44221 6.31727 6.27146L7.14818 3.92106L9.11818 11.352C9.16599 11.5317 9.27431 11.6921 9.4269 11.8089C9.57949 11.9257 9.76813 11.9928 9.96455 12H10C10.1909 12 10.3769 11.9434 10.5318 11.8382C10.6866 11.733 10.8024 11.5844 10.8627 11.4137L13.0018 5.36199L13.7891 6.47632C13.8721 6.59368 13.9846 6.6899 14.1166 6.75644C14.2485 6.82298 14.3958 6.85779 14.5455 6.85777H19.0909C19.332 6.85777 19.5632 6.76746 19.7337 6.6067C19.9042 6.44595 20 6.22792 20 6.00059C20 5.77325 19.9042 5.55522 19.7337 5.39447C19.5632 5.23371 19.332 5.1434 19.0909 5.1434Z" fill="#344054" />
                        </svg>

                    </span>
                    <h5 className="mb-0 text-gray-900 text-base font-semibold">{getTitle()}</h5>
                </div>
                <button onClick={onDialogClose} className="text-gray-400 hover:text-gray-600">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </button>
            </div>

            {/* Form Content */}
            <div className="px-6 pb-5">
                {/* Assignment Name */}
                <div className="flex mb-5">
                    <label className="font-lato font-medium text-[14px] leading-[150%] text-[#344054] w-1/3 pt-2">
                        Assignment Name*
                    </label>
                    <div className="w-2/3 pl-2">
                        <input
                            className={`h-9 pl-3 w-full input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${isView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                            value={formData.activityName}
                            placeholder="Enter Assignment Name"
                            onChange={(e) => handleInputChange("activityName", e.target.value)}
                            disabled={isView}
                        />
                        {formError.activityName && !isView && (
                            <p className="text-xs text-red-600 mb-2 mt-1">{formError.activityName}</p>
                        )}
                    </div>
                </div>

                {/* Category Radio Buttons */}
                <div className="flex mb-3">
                    <label className="font-medium text-gray-700 text-sm w-1/3 pt-2">Session Type*</label>
                    <div className="w-2/3 pl-2 flex gap-6 pt-2">
                        {/* Session Radio */}
                        <label className={`flex items-center gap-2 ${isView ? 'pointer-events-none' : 'cursor-pointer'}`}>
                            <div className="relative flex items-center justify-center w-4 h-4">
                                <input
                                    type="radio"
                                    name="accessCategory"
                                    value="session"
                                    checked={accessCategory === 'session'}
                                    onChange={() => handleCategoryChange('session')}
                                    disabled={isView}
                                    className={`appearance-none w-4 h-4 border rounded-full outline-none transition-colors duration-200 cursor-pointer
                                        ${accessCategory === 'session' ? 'border-[#FF8E1C]' : 'border-gray-300'}
                                        ${isView ? 'cursor-not-allowed' : ''}
                                    `}
                                />
                                {accessCategory === 'session' && (
                                    <div className="absolute w-2 h-2 rounded-full bg-[#FF8E1C]"></div>
                                )}
                            </div>
                            <span className="text-sm text-gray-700"> Company Session</span>
                        </label>

                        {/* Science of Self Radio */}
                        <label className={`flex items-center gap-2 ${isView ? 'pointer-events-none' : 'cursor-pointer'}`}>
                            <div className="relative flex items-center justify-center w-4 h-4">
                                <input
                                    type="radio"
                                    name="accessCategory"
                                    value="sos"
                                    checked={accessCategory === 'sos'}
                                    onChange={() => handleCategoryChange('sos')}
                                    disabled={isView}
                                    className={`appearance-none w-4 h-4 border rounded-full outline-none transition-colors duration-200 cursor-pointer
                                        ${accessCategory === 'sos' ? 'border-[#FF8E1C]' : 'border-gray-300'}
                                        ${isView ? 'cursor-not-allowed' : ''}
                                    `}
                                />
                                {accessCategory === 'sos' && (
                                    <div className="absolute w-2 h-2 rounded-full bg-[#FF8E1C]"></div>
                                )}
                            </div>
                            <span className="text-sm text-gray-700">Science of Self</span>
                        </label>

                        {/* Sales Training Companies Radio */}
                        <label className={`flex items-center gap-2 ${isView ? 'pointer-events-none' : 'cursor-pointer'}`}>
                            <div className="relative flex items-center justify-center w-4 h-4">
                                <input
                                    type="radio"
                                    name="accessCategory"
                                    value="stc"
                                    checked={accessCategory === 'stc'}
                                    onChange={() => handleCategoryChange('stc')}
                                    disabled={isView}
                                    className={`appearance-none w-4 h-4 border rounded-full outline-none transition-colors duration-200 cursor-pointer
                                        ${accessCategory === 'stc' ? 'border-[#FF8E1C]' : 'border-gray-300'}
                                        ${isView ? 'cursor-not-allowed' : ''}
                                    `}
                                />
                                {accessCategory === 'stc' && (
                                    <div className="absolute w-2 h-2 rounded-full bg-[#FF8E1C]"></div>
                                )}
                            </div>
                            <span className="text-sm text-gray-700">Sales Training Companies</span>
                        </label>
                    </div>
                </div>

                {/* Access Groups - Only Visible if Category is Session */}
                {accessCategory === 'session' && (
                    <div className="flex mb-5">
                        <label className="font-lato font-medium text-[14px] leading-[150%] text-[#344054] w-1/3 pt-2">
                            Access For*
                        </label>
                        <div className="w-2/3 pl-2">
                            <div className="flex flex-wrap gap-4 pt-2">
                                {accessOptions?.map((option) => (
                                    <label key={option.value} className={`inline-flex items-center ${isView ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                                        <input
                                            type="checkbox"
                                            className={`w-4 h-4 text-orange-500 border-gray-300 rounded focus:ring-orange-500 ${isView ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                            checked={formData.accessFor.includes(option.value)}
                                            onChange={() => handleAccessChange(option.value)}
                                            disabled={isView}
                                        />
                                        <span className="ml-2 text-sm text-gray-700">{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            {formError.accessFor && !isView && (
                                <p className="text-xs text-red-600 mb-2 mt-1">{formError.accessFor}</p>
                            )}
                        </div>
                    </div>
                )}

                {/* Questions */}
                <div className="space-y-0">
                    {formData.questions.map((question, mainIdx) => (
                        <div key={mainIdx}>
                            {/* Question Type row */}
                            <div className="flex items-center mb-4">
                                <label className="font-lato font-medium text-[14px] leading-[150%] text-[#344054] w-1/3 flex-shrink-0">
                                    Question Type*
                                </label>
                                <div className="flex-1 flex items-center gap-2">
                                    <div className="flex-1">
                                        <Select
                                            placeholder="Select Question Type"
                                            options={[
                                                { value: "simple", label: "Simple Question" },
                                                { value: "with_sub", label: "Question with Sub Questions" }
                                            ]}
                                            value={question.question_type
                                                ? { value: question.question_type, label: question.question_type === "simple" ? "Simple Question" : "Question with Sub Questions" }
                                                : null
                                            }
                                            onChange={(opt) => handleQuestionTypeChange(mainIdx, opt?.value || "simple")}
                                            isDisabled={isView}
                                            isClearable={false}
                                        />
                                    </div>
                                    {!isView && (
                                        <button
                                            onClick={() => removeMainQuestion(mainIdx)}
                                            disabled={formData.questions.length === 1}
                                            className="w-10 h-10 flex items-center justify-center rounded-lg text-white flex-shrink-0 transition-opacity"
                                            style={{ backgroundColor: '#FF8E1C', opacity: formData.questions.length === 1 ? 0.4 : 1 }}
                                            aria-label="Remove question"
                                        >
                                            <svg width="16" height="18" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M11.6673 4.16634V3.49967C11.6673 2.56625 11.6673 2.09954 11.4857 1.74302C11.3259 1.42942 11.0709 1.17445 10.7573 1.01466C10.4008 0.833008 9.93407 0.833008 9.00065 0.833008H7.66732C6.7339 0.833008 6.26719 0.833008 5.91067 1.01466C5.59706 1.17445 5.3421 1.42942 5.18231 1.74302C5.00065 2.09954 5.00065 2.56625 5.00065 3.49967V4.16634M6.66732 8.74967V12.9163M10.0007 8.74967V12.9163M0.833984 4.16634H15.834M14.1673 4.16634V13.4997C14.1673 14.8998 14.1673 15.5999 13.8948 16.1347C13.6552 16.6051 13.2727 16.9875 12.8023 17.2272C12.2675 17.4997 11.5674 17.4997 10.1673 17.4997H6.50065C5.10052 17.4997 4.40045 17.4997 3.86567 17.2272C3.39527 16.9875 3.01282 16.6051 2.77313 16.1347C2.50065 15.5999 2.50065 14.8998 2.50065 13.4997V4.16634" stroke="white" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Question input row */}
                            <div className="flex items-start mb-3">
                                <label className="font-lato font-medium text-[14px] leading-[150%] text-[#344054] w-1/3 flex-shrink-0 pt-2">
                                    Question*
                                </label>
                                <div className="flex-1">
                                    <input
                                        className={`w-full h-10 pl-3 input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${isView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        value={question.main_question}
                                        placeholder="Question"
                                        onChange={(e) => handleMainQuestionChange(mainIdx, e.target.value)}
                                        disabled={isView}
                                    />
                                    {formError.questions[mainIdx]?.main_question && !isView && (
                                        <p className="text-xs text-red-600 mt-1">{formError.questions[mainIdx].main_question}</p>
                                    )}
                                </div>
                            </div>

                            {/* Sub Questions — only when type is with_sub */}
                            {question.question_type === "with_sub" && (
                                <div className="mb-3">
                                    <div className="flex flex-col gap-2 mb-2">
                                        {question.sub_questions.map((subQ, subIdx) => (
                                            <div key={subIdx} className="flex items-center gap-2">
                                                <div className="w-1/3 flex-shrink-0" />
                                                <input
                                                    className={`flex-1 h-10 pl-3 input input-shadow text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline ${isView ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                                    value={subQ}
                                                    placeholder={`Sub Question ${subIdx + 1}`}
                                                    onChange={(e) => handleSubQuestionChange(mainIdx, subIdx, e.target.value)}
                                                    disabled={isView}
                                                />
                                                {question.sub_questions.length > 1 && !isView && (
                                                    <button
                                                        onClick={() => removeSubQuestion(mainIdx, subIdx)}
                                                        className="w-10 h-10 flex items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-red-500 hover:border-red-200 transition-colors flex-shrink-0"
                                                        aria-label="Remove sub question"
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                )}
                                                {formError.questions[mainIdx]?.sub_questions?.[subIdx] && !isView && (
                                                    <p className="text-xs text-red-600 mt-1">{formError.questions[mainIdx].sub_questions[subIdx]}</p>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    {!isView && (
                                        <div className="flex">
                                            <div className="w-1/3 flex-shrink-0" />
                                            <button
                                                onClick={() => addSubQuestion(mainIdx)}
                                                className="flex-1 py-2.5 text-sm font-medium rounded-lg flex items-center justify-center gap-1"
                                                style={{ backgroundColor: '#FFF4E5', color: '#FF8E1C' }}
                                            >
                                                Add Sub Question
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Divider between questions */}
                            {mainIdx < formData.questions.length - 1 && (
                                <hr className="border-gray-100 mb-4" />
                            )}
                        </div>
                    ))}

                    {/* Add Another Question */}
                    {!isView && (
                        <div className="flex mt-2">
                            <div className="w-1/3 flex-shrink-0" />
                            <button
                                onClick={addMainQuestion}
                                className="flex-1 py-2.5 text-sm font-medium rounded-lg flex items-center justify-center gap-1"
                                style={{ backgroundColor: '#FFF4E5', color: '#FF8E1C' }}
                            >
                                Add Another Question
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex dailog-footer">
                <button
                    className="mr-1.5 whiteBtnShadow btn border border-gray-100 block w-full rounded-lg h-10 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 hover:border-gray-300 focus:bg-white focus:border-gray-300 duration-100 secondary-btn"
                    onClick={onDialogClose}
                    style={{ backgroundColor: '#FFF4E5' }}
                >
                    {isView ? 'Close' : 'Cancel'}
                </button>

                {!isView && (
                    <BtnPrimary
                        disabled={buttonDisabled}
                        onClick={handleSubmit}
                        className="ml-1.5"
                        style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
                    >
                        {buttonDisabled ? <ThreeDotLoader /> : isEdit ? "Update Assignment" : "Add Assignment"}
                    </BtnPrimary>
                )}
            </div>
        </Dialog>
    );
};

export default AddEditAssignments;